import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { type MeResponse, type OfferRow, api } from "../lib/api";
import { AuctionPanel } from "./AuctionPanel";

type Me = MeResponse & {
	org: { id: string; kind: "brand" | "affiliate" } | null;
};

export function OfferDetail() {
	const { id = "" } = useParams();
	const nav = useNavigate();
	const qc = useQueryClient();
	const offerQ = useQuery<OfferRow>({
		queryKey: ["offer", id],
		queryFn: () => api.get(`/offers/${id}`),
		enabled: !!id,
	});
	const meQ = useQuery<Me>({ queryKey: ["me"], queryFn: () => api.get("/me") });

	const publishMut = useMutation({
		mutationFn: () => api.post(`/offers/${id}/publish`, {}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["offer", id] });
			qc.invalidateQueries({ queryKey: ["offers"] });
		},
	});
	const closeMut = useMutation({
		mutationFn: () => api.post(`/offers/${id}/close`, {}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["offer", id] });
			qc.invalidateQueries({ queryKey: ["offers"] });
		},
	});

	if (offerQ.isLoading) return <div className="p-6">Loading…</div>;
	if (offerQ.error)
		return <div className="p-6 text-red-700">Error: {(offerQ.error as Error).message}</div>;
	if (!offerQ.data) return <div className="p-6 text-neutral-500">Not found</div>;
	const o = offerQ.data;
	const myOrg = meQ.data?.org;
	const own = myOrg?.kind === "brand" && myOrg.id === o.brandOrgId;

	return (
		<div className="space-y-4 p-6">
			<div>
				<Link to="/offers" className="text-sm text-blue-700 hover:underline">
					← Offers
				</Link>
			</div>
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold">{o.title}</h1>
					<div className="font-mono text-xs text-neutral-500">{o.id}</div>
				</div>
				<div className="flex gap-2">
					{own && o.status === "draft" && (
						<>
							<button
								type="button"
								onClick={() => nav(`/offers/${o.id}/edit`)}
								className="rounded border border-neutral-300 bg-white px-3 py-1 text-sm hover:bg-neutral-50"
							>
								Edit
							</button>
							<button
								type="button"
								onClick={() => publishMut.mutate()}
								className="rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700"
							>
								Publish
							</button>
						</>
					)}
					{own && o.status === "active" && (
						<button
							type="button"
							onClick={() => closeMut.mutate()}
							className="rounded bg-neutral-600 px-3 py-1 text-sm text-white hover:bg-neutral-700"
						>
							Close
						</button>
					)}
				</div>
			</div>
			<dl className="grid grid-cols-2 gap-2 text-sm">
				<Field label="Vertical" value={o.vertical} />
				<Field label="Status" value={o.status} />
				<Field
					label="Payout"
					value={`${o.payoutType} ${o.payoutAmount ?? `${o.payoutPercent}%`} ${o.payoutCurrency}`}
				/>
				<Field label="Geos" value={safeArr(o.geos).join(", ")} />
				<Field label="Daily cap" value={o.dailyCap ?? "—"} />
				<Field label="Monthly cap" value={o.monthlyCap ?? "—"} />
				<Field label="Negotiation TTL (s)" value={o.negotiationTtlS} />
				<Field label="Created" value={new Date(o.createdAt * 1000).toISOString().slice(0, 19)} />
			</dl>
			{o.status === "active" && (
				<section>
					<h2 className="mb-2 text-base font-semibold">Auction</h2>
					<AuctionPanel offerId={o.id} side={own ? "brand" : "affiliate"} />
				</section>
			)}
		</div>
	);
}

function Field({ label, value }: { label: string; value: string | number }) {
	return (
		<div>
			<dt className="text-xs text-neutral-500">{label}</dt>
			<dd className="text-neutral-900">{value}</dd>
		</div>
	);
}

function safeArr(s: string | null): string[] {
	if (!s) return [];
	try {
		const v = JSON.parse(s);
		return Array.isArray(v) ? (v as string[]) : [];
	} catch {
		return [];
	}
}
